<template>
  <!-- Horizontal Layout: Steps in row at top, content below -->
  <div v-if="orientation === 'horizontal'" class="wizard-steps-horizontal" :class="{ 'clickable-steps': allowClick }">
    <Stepper :value="currentStep + 1" :linear="!allowClick">
      <StepList>
        <Step
          v-for="(step, index) in steps"
          :key="index"
          :value="index + 1"
          as="div"
          @click="handleStepClick(index)"
        >
          {{ step.title }}
        </Step>
      </StepList>
      <StepPanels>
        <StepPanel
          v-for="(step, index) in steps"
          :key="index"
          :value="index + 1"
        >
          <div class="step-content">
            <slot :name="`step-${index}`"></slot>
          </div>
        </StepPanel>
      </StepPanels>
    </Stepper>
  </div>

  <!-- Vertical Layout: Steps in column with integrated content -->
  <div v-else class="wizard-steps-vertical" :class="{ 'clickable-steps': allowClick }">
    <Stepper :value="currentStep + 1" :linear="!allowClick">
      <StepItem
        v-for="(step, index) in steps"
        :key="index"
        :value="index + 1"
      >
        <Step as="div" @click="handleStepClick(index)">
          <div class="step-content-wrapper">
            <div class="step-title">{{ step.title }}</div>
            <div v-if="showDescription && step.description" class="step-description">
              {{ step.description }}
            </div>
          </div>
        </Step>
        <StepPanel>
          <div class="step-content">
            <slot :name="`step-${index}`"></slot>
          </div>
        </StepPanel>
      </StepItem>
    </Stepper>
  </div>
</template>

<script setup lang="ts">
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import Step from 'primevue/step';
import StepPanels from 'primevue/steppanels';
import StepPanel from 'primevue/steppanel';
import StepItem from 'primevue/stepitem';

interface WizardStep {
  title: string;
  description?: string;
  key?: string;
  [key: string]: any;
}

const props = withDefaults(defineProps<{
  steps: WizardStep[];
  currentStep: number;
  orientation?: 'horizontal' | 'vertical';
  showDescription?: boolean;
  allowClick?: boolean;
}>(), {
  orientation: 'vertical',
  showDescription: true,
  allowClick: false,
});

const emit = defineEmits<{
  'step-click': [stepIndex: number];
}>();

const handleStepClick = (stepIndex: number): void => {
  if (props.allowClick) {
    emit('step-click', stepIndex);
  }
};
</script>

<style scoped>
/* Let PrimeVue handle all styling - no custom overrides */
.step-content {
  max-width: 100%;
  overflow-x: hidden;
}

/* By default, disable all step clicking - make steps non-interactive */
:deep(.p-step),
:deep(.p-step-header) {
  pointer-events: none;
  cursor: default !important;
}

:deep(.p-step *),
:deep(.p-step-header *) {
  cursor: default !important;
}

/* When clickable-steps class is present, enable clicking */
.clickable-steps :deep(.p-step),
.clickable-steps :deep(.p-step-header) {
  pointer-events: auto;
  cursor: pointer !important;
}

.clickable-steps :deep(.p-step *),
.clickable-steps :deep(.p-step-header *) {
  cursor: pointer !important;
}
</style>
